import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { ArrowBack, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Button, DayWithAvailabilityIndicator, MobileCalendarBody, MobileCalendarEventsList, useAlert } from '@uplift/ui-v2';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useBookingSlotsQuery } from '@uplift/queries';
import { CalendarStore } from '@uplift/store';
import { TimeRange, ProviderMedicalRole } from '@uplift/types';
import { QuickCalendarNavigation } from './QuickCalendarNavigation.component';


export type AvailabilityReviewProps = {
  date?: string;
  onBack: () => void;
  onSessionDetails: (range: TimeRange) => void;
};


export const AvailabilityReview: React.FC<AvailabilityReviewProps> = observer((props) => {
  const alert = useAlert();
  const calendarStore = React.useMemo(() => {
    return new CalendarStore();
  }, []);
  const timezone = useTimezone();
  const me = useCurrentuser();
  const today = React.useMemo(() => {
    return CalendarDateService.date().tz(timezone).format(DateFormats.ISO_DATE);
  }, [ timezone ]);
  const [ date, setDate ] = React.useState(() => {
    if (props.date) {
      return CalendarDateService.date(props.date, DateFormats.ISO_DATE).format(DateFormats.ISO_DATE);
    }

    const weeksShift = {
      [ProviderMedicalRole.therapist]: 1,
      [ProviderMedicalRole.psychiatrist]: 4,
    }[me.medicalRole];

    return CalendarDateService.date(today, DateFormats.ISO_DATE).add(weeksShift, 'week').format(DateFormats.ISO_DATE);
  });
  const [ currentMonth, setCurrentMonth ] = React.useState(date);
  const { data: bookingSlots } = useBookingSlotsQuery(date, timezone);

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box p={1} pb={0}>
        <Button startIcon={<ArrowBack />} color="secondary" onClick={props.onBack}>Back</Button>
      </Box>
      <Box
        px={3}
        display="flex"
        flexDirection="column"
        flex={1}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography component="strong" fontWeight={500}>{CalendarDateService.date(currentMonth, DateFormats.ISO_DATE).format('MMMM YYYY')}</Typography>
          <Box display="flex" gap="8px">
            <IconButton onClick={() => setCurrentMonth(CalendarDateService.date(currentMonth, DateFormats.ISO_DATE).subtract(1, 'month').format(DateFormats.ISO_DATE))}><KeyboardArrowLeft /></IconButton>
            <IconButton onClick={() => setCurrentMonth(CalendarDateService.date(currentMonth, DateFormats.ISO_DATE).add(1, 'month').format(DateFormats.ISO_DATE))}><KeyboardArrowRight /></IconButton>
          </Box>
        </Box>
        <Box>
          <QuickCalendarNavigation
            medicalRole={me.medicalRole}
            today={today}
            date={date}
            goToDate={(date) => {
              setDate(date);
              setCurrentMonth(date);
            }}
          />
        </Box>
        <MobileCalendarBody
          scrollable={false}
          today={today}
          date={date}
          setDate={setDate}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          expanded
          components={{
            Day: DayWithAvailabilityIndicator,
          }}
          disablePastDays
        />
        <Divider />
        <Box flex={1}>
          <MobileCalendarEventsList
            today={today}
            date={date}
          />
        </Box>

      </Box>
      <Box>
        <Divider />
        <Box p={3}>
          <Button
            color="secondary"
            variant="contained"
            fullWidth
            onClick={async () => {
              calendarStore.allowedSlots = bookingSlots;
              calendarStore.getFirstAvailableCreateAppointmentRange({
                start: CalendarDateService.date.tz(date, DateFormats.ISO_DATE, timezone),
                timezone: timezone,
                medicalRole: me.medicalRole,
              })
                .then((event => {
                  props.onSessionDetails(event);
                }))
                .catch(_err => {
                  alert.error('No available slots for this day, please choose another day');
                });
            }}
          >
            NEXT: SESSION DETAILS
          </Button>
        </Box>
      </Box>
    </Box>
  );
});
